import { Client, UpdateNotificationPreferences } from '../../api/api-clients';
import { Injectable, inject } from '@angular/core';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';

import type { AppNotificationPreferences } from '../models/notification-preferences.model';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../models/notification-preferences.model';
import { ProfileService } from './profile.service';
import { firstValueFrom } from 'rxjs';
import { notificationPreferencesKeys } from './query-keys';

@Injectable({ providedIn: 'root' })
export class NotificationPreferencesService {
  private readonly client = inject(Client);
  private readonly profileService = inject(ProfileService);
  private readonly queryClient = injectQueryClient();

  readonly myNotificationPreferences = injectQuery(() => {
    const profileId = this.profileService.myProfile.data()?.id;
    return {
      queryKey: notificationPreferencesKeys.forProfile(profileId),
      queryFn: async (): Promise<AppNotificationPreferences> => {
        const data = await firstValueFrom(this.client.notificationPreferencesGET());
        return {
          profileId: data.profileId ?? profileId!,
          globalEnabled: data.globalEnabled ?? DEFAULT_NOTIFICATION_PREFERENCES.globalEnabled,
          activityProfileViews: data.activityProfileViews ?? DEFAULT_NOTIFICATION_PREFERENCES.activityProfileViews,
          activityFollowers: data.activityFollowers ?? DEFAULT_NOTIFICATION_PREFERENCES.activityFollowers,
          activityMessages: data.activityMessages ?? DEFAULT_NOTIFICATION_PREFERENCES.activityMessages,
          activityRatings: data.activityRatings ?? DEFAULT_NOTIFICATION_PREFERENCES.activityRatings,
          activityFrequency: data.activityFrequency,
          summaryDaily: data.summaryDaily ?? DEFAULT_NOTIFICATION_PREFERENCES.summaryDaily,
          summaryWeekly: data.summaryWeekly ?? DEFAULT_NOTIFICATION_PREFERENCES.summaryWeekly,
          marketingPromotions: data.marketingPromotions ?? DEFAULT_NOTIFICATION_PREFERENCES.marketingPromotions,
          pushEnabled: data.pushEnabled ?? DEFAULT_NOTIFICATION_PREFERENCES.pushEnabled,
          emailEnabled: data.emailEnabled ?? DEFAULT_NOTIFICATION_PREFERENCES.emailEnabled,
        };
      },
      enabled: !!profileId,
    };
  });

  /**
   * Updates notification preferences for a profile.
   * Invalidates the preferences cache for that profile on success.
   */
  readonly updateNotificationPreferences = injectMutation(() => ({
    mutationFn: (preferences: AppNotificationPreferences) => {
      const body = new UpdateNotificationPreferences({
        globalEnabled: preferences.globalEnabled,
        activityProfileViews: preferences.activityProfileViews,
        activityFollowers: preferences.activityFollowers,
        activityMessages: preferences.activityMessages,
        activityRatings: preferences.activityRatings,
        activityFrequency: preferences.activityFrequency,
        summaryDaily: preferences.summaryDaily,
        summaryWeekly: preferences.summaryWeekly,
        marketingPromotions: preferences.marketingPromotions,
        pushEnabled: preferences.pushEnabled,
        emailEnabled: preferences.emailEnabled,
      });
      return firstValueFrom(this.client.notificationPreferencesPUT(body));
    },
    onSuccess: (_data, variables) => {
      this.queryClient.invalidateQueries({
        queryKey: notificationPreferencesKeys.forProfile(variables.profileId),
      });
    },
  }));
}
